
import React from 'react';
import { Link } from 'react-router-dom';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { decodeHtmlEntities } from '@/lib/utils/decodeHtmlEntities';
import { Paper } from '@/types/paper';
import TexText from '@/components/common/TexText';

interface PaperCardTitleProps {
  paper: Paper;
  featured?: boolean;
}

const PaperCardTitle: React.FC<PaperCardTitleProps> = ({ paper, featured = false }) => {
  const cleanTitle = decodeHtmlEntities(paper.title.replace(/<\/?[^>]+(>|$)/g, ''));
  
  const titleElement = (
    <h3 
      className={cn(
        "font-medium tracking-tight group-hover:text-primary transition-colors duration-300", 
        featured 
          ? "text-lg md:text-xl lg:text-2xl mb-3 md:mb-4" 
          : "text-base md:text-lg mb-3 line-clamp-3"
      )}
      data-testid="paper-title"
    >
      <span itemProp="name headline">
        <TexText>{cleanTitle}</TexText>
      </span>
    </h3>
  );

  const linkElement = (
    <Link 
      to={`/paper/${paper.id}`}
      className="block"
      title={`Read paper: ${cleanTitle}`}
      aria-label={`Read paper: ${cleanTitle}`}
      itemProp="url"
    >
      {titleElement}
    </Link>
  );

  if (featured) {
    return linkElement;
  }

  return (
    <HoverCard openDelay={200} closeDelay={300}>
      <HoverCardTrigger asChild>
        {linkElement}
      </HoverCardTrigger>
      
      <HoverCardContent 
        align="start" 
        sideOffset={10}
        className={cn(
          "w-80 md:w-96 max-w-[90vw] md:max-w-[600px] bg-white/95 backdrop-blur-sm border border-primary/20 shadow-lg rounded-xl overflow-hidden",
          "z-[9999]"
        )}
      >
        <div className="space-y-2">
          <h4 className="font-medium text-sm">Abstract</h4>
          <ScrollArea className="h-[200px] md:h-[240px] pr-4">
            <p className="text-sm text-muted-foreground" itemProp="abstract">
              <TexText>{paper.abstract}</TexText>
            </p>
          </ScrollArea>
          <div className="pt-2 text-xs text-primary hover:underline">
            <Link 
              to={`/paper/${paper.id}`} 
              className="flex items-center gap-1"
              title={`Read full paper: ${cleanTitle}`}
            >
              Read full paper <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

export default PaperCardTitle;
